import { TRIP_ID_QUERY_PARAM } from '../services/selected-trip-url/selected-trip-url.helpers';

const LEGACY_TRIP_ID_QUERY_PARAM = 'tripID';

/** Paths v2 already serves under the same name — no path rewrite, only query normalization. */
const OWNED_PATHS = new Set([
  'reset',
  'verify-account-question',
  'invalid-token',
  'trip-info',
  'coffee-fundraising',
  'survey',
  'trip-survey',
]);

interface LegacyAliasTarget {
  path: string;
  fragment?: string;
}

/** Legacy path segment → canonical v2 location. See full-launch-checklist legacy route map. */
const ALIASED_PATHS: Readonly<Record<string, LegacyAliasTarget>> = {
  'group-info': { path: 'group-management' },
  'finances-ppm': { path: 'trip-finances' },
  'finances-group': { path: 'trip-finances' },
  'funding-permissions': { path: 'trip-finances' },
  fundraising: { path: 'prep-planning', fragment: 'fundraising' },
  'my-info': { path: 'profile' },
  'personal-payment': { path: 'payment' },
  messages: { path: 'chatter' },
  'partnership-info': { path: 'partner-info' },
  'travel-resources': { path: 'resources' },
  'trip-prep': { path: 'prep-planning' },
  'background-check': { path: 'forms', fragment: 'background-check' },
  'gl-questionnaire': { path: 'forms', fragment: 'gl-questionnaire' },
  permissions: { path: 'group-management' },
  verify: { path: 'verify-account' },
  'verification-failed': { path: 'invalid-verification-token' },
  signout: { path: 'login' },
  'trip-terms-conditions': { path: 'forms', fragment: 'terms' },
  'trip-statement-of-faith': { path: 'forms', fragment: 'statement-of-faith' },
  'trip-background-check-agreement': { path: 'forms', fragment: 'background-check-agreement' },
  legal: { path: 'forms', fragment: 'agreements' },
  'risk-assessment': { path: 'forms', fragment: 'agreements' },
  'covid-payment-policy': { path: 'trip-finances' },
  'fundraising-agreement': { path: 'trip-finances' },
};

/** Stays on legacy — v2 must not claim these URLs. */
const FORCED_LEGACY_PATHS = new Set(['fundraising-profile-response']);

/** Deliberately retired — fall through to the wildcard unchanged. */
const DROPPED_PATHS = new Set([
  'documents',
  'ppm365-partner-dashboard',
  'meeting-mode',
  'covid-19',
  'trip-journal',
  'redirect',
]);

function normalizePath(path: string): string {
  const trimmed = path.trim();
  if (!trimmed || trimmed === '/') {
    return '/';
  }

  const withLeading = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  const withoutTrailing = withLeading.replace(/\/+$/, '');
  return withoutTrailing.length > 0 ? withoutTrailing : '/';
}

function pathSegment(path: string): string {
  const normalized = normalizePath(path);
  return normalized === '/' ? '' : normalized.slice(1);
}

function toUrlSearchParams(
  query: URLSearchParams | Readonly<Record<string, string>>,
): URLSearchParams {
  if (query instanceof URLSearchParams) {
    return new URLSearchParams(query);
  }

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    params.set(key, value);
  }
  return params;
}

function normalizeTripIdQueryParam(params: URLSearchParams): URLSearchParams {
  const normalized = new URLSearchParams(params);

  if (normalized.has(TRIP_ID_QUERY_PARAM)) {
    normalized.delete(LEGACY_TRIP_ID_QUERY_PARAM);
    return normalized;
  }

  const legacyTripId = normalized.get(LEGACY_TRIP_ID_QUERY_PARAM);
  if (legacyTripId !== null) {
    normalized.delete(LEGACY_TRIP_ID_QUERY_PARAM);
    normalized.set(TRIP_ID_QUERY_PARAM, legacyTripId);
  }

  return normalized;
}

interface ResolvedLegacyTarget {
  segment: string;
  fragment?: string;
}

function buildUrl(path: string, params: URLSearchParams, fragment?: string): string {
  const normalizedPath = normalizePath(path);
  const query = params.toString();
  const base = query.length > 0 ? `${normalizedPath}?${query}` : normalizedPath;
  return fragment ? `${base}#${fragment}` : base;
}

function resolveTarget(segment: string): ResolvedLegacyTarget | null {
  if (
    FORCED_LEGACY_PATHS.has(segment) ||
    DROPPED_PATHS.has(segment)
  ) {
    return null;
  }

  if (segment in ALIASED_PATHS) {
    const alias = ALIASED_PATHS[segment];
    return { segment: alias.path, fragment: alias.fragment };
  }

  if (OWNED_PATHS.has(segment)) {
    return { segment };
  }

  return null;
}

/**
 * Maps a legacy Pelican Account path and query string to the canonical v2 location.
 * Returns null when the URL should pass through unchanged (owned path, dropped,
 * forced-legacy, or unknown).
 */
export function canonicalizeLegacyUrl(
  path: string,
  query: URLSearchParams | Readonly<Record<string, string>>,
): string | null {
  const segment = pathSegment(path);
  const incomingParams = toUrlSearchParams(query);
  const normalizedParams = normalizeTripIdQueryParam(incomingParams);
  const target = resolveTarget(segment);

  const pathChanged = target !== null && target.segment !== segment;
  const fragmentChanged = target?.fragment !== undefined;
  const queryChanged = normalizedParams.toString() !== incomingParams.toString();

  if (!pathChanged && !queryChanged && !fragmentChanged) {
    return null;
  }

  const targetPath = target === null ? normalizePath(path) : `/${target.segment}`;

  return buildUrl(targetPath, normalizedParams, target?.fragment);
}

/** Parses a router URL (path + query, optional hash) for {@link canonicalizeLegacyUrl}. */
export function canonicalizeLegacyRouterUrl(url: string): string | null {
  const hashIndex = url.indexOf('#');
  const withoutHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
  const fragment = hashIndex >= 0 ? url.slice(hashIndex) : '';

  const queryIndex = withoutHash.indexOf('?');
  const path = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
  const queryString = queryIndex >= 0 ? withoutHash.slice(queryIndex + 1) : '';

  const canonical = canonicalizeLegacyUrl(path, new URLSearchParams(queryString));
  if (canonical === null) {
    return null;
  }

  if (canonical.includes('#')) {
    return canonical;
  }

  return fragment.length > 0 ? `${canonical}${fragment}` : canonical;
}
